import React, { useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import LockIcon from "@material-ui/icons/Lock";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import CssBaseline from "@material-ui/core/CssBaseline";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { selectIsError, fetchAsyncPasswordChange } from "./authSlice";

import { PASSWORDRESET_PARA } from "../../types/types";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: "100vh",
  },

  paper: {
    margin: theme.spacing(8, 4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "#2699FB",
  },
  error: {
    margin: theme.spacing(3, 0),
    color: "#cc3333",
    fontSize: 12,
  },
  passError: {
    fontSize: 12,
    width: "100%",
    color: "#f44336",
  },
  subText: {
    fontSize: 12,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    width: "100%",
    textAlign: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: theme.spacing(2),
  },
  headerBack: {
    paddingLeft: theme.spacing(3),
  },
  circulContainer: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    zIndex: 3,
  },
  circul: {
    height: "100px !important",
    width: "100px !important",
  },
  hero: {
    backgroundImage: "url(https://source.unsplash.com/bq53S9hEYx4)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "100%",
    position: "absolute",
    width: "100%",
    zIndex: 0,
  },
}));

const PasswordResetConfirm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const params: PASSWORDRESET_PARA = useParams();
  const isError = useSelector(selectIsError);
  const [isSend, setIsSend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [data, setData] = useState({
    new_password1: "",
    new_password2: "",
    uid: params.uid,
    token: params.token,
  });

  //送信ボタン
  //送れて送れなくてもメール送信完了を表示
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await setIsLoading(true);
    if (data.new_password1 === data.new_password2) {
      await setPasswordError(false);
      await dispatch(fetchAsyncPasswordChange(data));
      await setIsLoading(false);
      await setIsSend(true);
    } else {
      await setPasswordError(true);
      await setIsLoading(false);
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let str = e.target.value;
    let name = e.target.name;

    str = str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function (s) {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
    });
    str = str.replace(/[^!-~]/g, "");

    await setData({ ...data, [name]: str });
  };
  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />

      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        {isLoading && (
          <div className={classes.circulContainer}>
            <CircularProgress className={classes.circul} />
          </div>
        )}
        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            パスワード再発行
          </Typography>
          {isSend === true ? (
            isError === true ? (
              <div>
                <Typography className={classes.subText}>
                  リンクの有効期限が切れています。
                  <br />
                  再度手続きをお願いします。
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  onClick={() => (window.location.href = "/login")}
                >
                  ログイン画面へ
                </Button>
              </div>
            ) : (
              <div>
                <Typography className={classes.subText}>
                  パスワードを更新しました。
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  onClick={() => (window.location.href = "/login")}
                >
                  ログイン画面へ
                </Button>
              </div>
            )
          ) : (
            //まだ送っていない時
            <div>
              <Typography className={classes.subText}>
                新しいパスワードを設定します。
              </Typography>
              <form className={classes.form} onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  fullWidth
                  required
                  id="new_password1"
                  label="新しいパスワード(半角英数字8文字以上)"
                  name="new_password1"
                  type="password"
                  autoComplete="new_password1"
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    inputProps: { minLength: 8, maxLength: 20 },
                  }}
                  value={data.new_password1}
                  onChange={handleInputChange}
                />

                <TextField
                  margin="normal"
                  fullWidth
                  required
                  id="new_password2"
                  label="新しいパスワード(確認)"
                  name="new_password2"
                  type="password"
                  autoComplete="new_password2"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    inputProps: { minLength: 8, maxLength: 20 },
                  }}
                  value={data.new_password2}
                  onChange={handleInputChange}
                />
                {passwordError && (
                  <div className={classes.passError}>
                    新しいパスワード(確認)が一致しません。
                  </div>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                >
                  パスワードを設定する
                </Button>
              </form>
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default PasswordResetConfirm;
